import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

/**
 * Los asistentes IA del store en xcon-api (change conversaciones-agente-ia, D33, D46 y D62–D63).
 * Leer pide `store_operators` Viewer y guardar, Admin: el mismo gate que Operadores del Store.
 * Desde D62 la configuración es de cada asistente —un usuario con `agent: admin`— y cuelga de
 * `…/agents/:userId/…`; lo del pod (latido, credencial del worker) sigue siendo del store.
 */
const agentBase = (storeCode: string) =>
  `${API_BASE_URL}/stores/${encodeURIComponent(storeCode)}`;

const asistenteBase = (storeCode: string, userId: number) =>
  `${agentBase(storeCode)}/agents/${userId}`;

export const AGENT_PROVIDERS = ["anthropic", "openai", "google"] as const;
export type AgentProvider = (typeof AGENT_PROVIDERS)[number];

export interface AgentLimits {
  maxToolCallsPerTurn: number | null;
  maxTokensPerDay: number | null;
  maxTurnsPerConversation: number | null;
  ratePerMin: number | null;
}

export interface AgentFuse {
  userId: number;
  disabled: boolean;
  disabledUntil: string | null;
  trippedAt: string | null;
  reason: string | null;
}

export interface AgentConfig {
  storeCode: string;
  configured: boolean;
  enabled: boolean;
  enabledAt?: string | null;
  displayName?: string | null;
  provider?: string | null;
  model?: string | null;
  hasApiKey: boolean;
  /** `••••abcd`: la key no vuelve nunca. */
  apiKeyHint: string | null;
  preferredSeller?: string | null;
  limits?: AgentLimits;
  knowledgeVersion?: number;
  fuse: AgentFuse[];
  defaults: {
    ratePerMin: number;
    fuseRequestsPerMin: number;
    fuseErrorsPerMin: number;
    fuseMinutes: number;
  };
  updatedBy?: number | null;
  updatedAt?: string | null;
}

/** Lo que no viene no se toca; `apiKey: null` la borra y `null` en un límite vuelve al default. */
export interface UpdateAgentConfig extends Partial<AgentLimits> {
  enabled?: boolean;
  displayName?: string;
  provider?: string;
  model?: string;
  apiKey?: string | null;
  preferredSeller?: string | null;
}

export interface AgentBudget {
  day: string;
  timezone: string;
  tokensToday: number;
  maxTokensPerDay: number | null;
  exhausted: boolean;
  resetsAt: string;
}

export interface AgentStatus {
  storeCode: string;
  online: boolean;
  lastSeenAt: string | null;
  baseVersion: string | null;
  startedAt: string | null;
  /** El usuario con el que corre el pod: el que se ofrece como asistente del canal. */
  agentUserId: number | null;
  fuse: AgentFuse[];
  budget: AgentBudget;
}

export interface AgentRunsByModel {
  provider: string | null;
  model: string | null;
  runs: number;
  tokensIn: number;
  tokensOut: number;
  tokensCached: number;
  costEstimate: number;
}

export interface AgentRunsSummary {
  storeCode: string;
  month: string;
  timezone: string;
  totals: Omit<AgentRunsByModel, "provider" | "model">;
  byModel: AgentRunsByModel[];
}

/**
 * Un asistente del store en `GET …/agents`: los usuarios con `agent: admin` activo en él. Uno sin
 * configurar todavía viene apagado, sin proveedor y sin key.
 */
export interface AgentSummary {
  userId: number;
  name: string | null;
  email: string | null;
  displayName: string | null;
  enabled: boolean;
  provider: string | null;
  /** El modelo de las notas (`assist`). */
  model: string | null;
  /** D48: el del modo automático. */
  modelAuto?: string | null;
  hasKey: boolean;
  apiKeyHint: string | null;
  knowledgeVersion: number | null;
  updatedAt: string | null;
}

/** La credencial del worker del store (D63): de la clave sólo se guarda cómo termina. */
export interface WorkerKeyStatus {
  exists: boolean;
  hint: string | null;
  createdAt: string | null;
  createdBy: number | null;
  lastUsedAt: string | null;
}

/** La clave recién generada: es la única vez que viene. */
export interface WorkerKeyCreated {
  key: string;
  hint: string;
  createdAt: string;
}

export type AgentTestStatus =
  | "pending"
  | "running"
  | "done"
  | "failed"
  | "expired";

/** Una prueba de «Probar» (D46): el pod la corre en seco y contesta por la API. */
export interface AgentTest {
  testId: string;
  storeCode: string;
  input: string;
  context: { contactId?: number };
  status: AgentTestStatus;
  reply: string | null;
  toolsCalled: string[];
  costEstimate: number | null;
  error: string | null;
  createdBy: number | null;
  createdAt: string | null;
  answeredAt: string | null;
}

export interface CreateAgentTest {
  input: string;
  contactId?: number;
}

export const KNOWLEDGE_SECTIONS = [
  "negocio",
  "presupuestar",
  "recetas",
  "faq",
  "no_hacer",
  "tono",
] as const;
export type KnowledgeSection = (typeof KNOWLEDGE_SECTIONS)[number];
export type Knowledge = Partial<Record<KnowledgeSection, string>> & {
  /** D58: «Notas de la sucursal», por código de seller. Lo que su config no dice. */
  branchNotes?: Record<string, string>;
};

export interface KnowledgeVersion {
  version: number;
  restoredFrom: number | null;
  updatedBy: number | null;
  createdAt: string | null;
}

export interface AgentKnowledge {
  storeCode: string;
  userId?: number;
  knowledge: Knowledge;
  version: number;
  sections: { key: KnowledgeSection; maxLength: number }[];
  versions: KnowledgeVersion[];
}

export const agentApi = {
  /** Los asistentes del store, con su estado: la lista de `Stores → Asistente IA`. */
  asistentes: (storeCode: string) =>
    axiosInstance
      .get<AgentSummary[]>(`${agentBase(storeCode)}/agents`)
      .then((r) => r.data ?? []),

  config: (storeCode: string, userId: number) =>
    axiosInstance
      .get<AgentConfig>(`${asistenteBase(storeCode, userId)}/config`)
      .then((r) => r.data),

  guardar: (storeCode: string, userId: number, body: UpdateAgentConfig) =>
    axiosInstance
      .put<AgentConfig>(`${asistenteBase(storeCode, userId)}/config`, body)
      .then((r) => r.data),

  levantarFusible: (storeCode: string, userId: number) =>
    axiosInstance
      .put<{ reset: unknown; fuse?: AgentFuse[] }>(
        `${asistenteBase(storeCode, userId)}/reset-fuse`,
      )
      .then((r) => r.data),

  /** El latido es del pod, uno por store (D26). */
  estado: (storeCode: string) =>
    axiosInstance
      .get<AgentStatus>(`${agentBase(storeCode)}/agent-status`)
      .then((r) => r.data),

  resumen: (
    storeCode: string,
    filtro: { month?: string; agentUserId?: number } = {},
  ) =>
    axiosInstance
      .get<AgentRunsSummary>(`${agentBase(storeCode)}/agent-runs/summary`, {
        params: {
          ...(filtro.month ? { month: filtro.month } : {}),
          ...(filtro.agentUserId ? { agentUserId: filtro.agentUserId } : {}),
        },
      })
      .then((r) => r.data),

  conocimiento: (storeCode: string, userId: number) =>
    axiosInstance
      .get<AgentKnowledge>(`${asistenteBase(storeCode, userId)}/knowledge`)
      .then((r) => r.data),

  guardarConocimiento: (
    storeCode: string,
    userId: number,
    knowledge: Knowledge,
  ) =>
    axiosInstance
      .put<AgentKnowledge>(
        `${asistenteBase(storeCode, userId)}/knowledge`,
        knowledge,
      )
      .then((r) => r.data),

  restaurar: (storeCode: string, userId: number, version: number) =>
    axiosInstance
      .post<AgentKnowledge>(
        `${asistenteBase(storeCode, userId)}/knowledge/restore/${version}`,
      )
      .then((r) => r.data),

  /** D62: una versión nueva con el conocimiento de otro asistente del store. La key no viaja. */
  copiarConocimiento: (
    storeCode: string,
    userId: number,
    sourceUserId: number,
  ) =>
    axiosInstance
      .post<AgentKnowledge>(
        `${asistenteBase(
          storeCode,
          userId,
        )}/knowledge/copy-from/${sourceUserId}`,
      )
      .then((r) => r.data),

  probar: (storeCode: string, userId: number, body: CreateAgentTest) =>
    axiosInstance
      .post<AgentTest>(`${asistenteBase(storeCode, userId)}/tests`, body)
      .then((r) => r.data),

  prueba: (storeCode: string, userId: number, testId: string) =>
    axiosInstance
      .get<AgentTest>(`${asistenteBase(storeCode, userId)}/tests/${testId}`)
      .then((r) => r.data),

  credencial: (storeCode: string) =>
    axiosInstance
      .get<WorkerKeyStatus>(`${agentBase(storeCode)}/agent-worker-key`)
      .then((r) => r.data),

  /** Genera o rota: la anterior deja de valer y se cortan las sesiones de los asistentes. */
  generarCredencial: (storeCode: string) =>
    axiosInstance
      .post<WorkerKeyCreated>(`${agentBase(storeCode)}/agent-worker-key`)
      .then((r) => r.data),
};
